import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { ChangePasswordModal } from '../features/ChangePasswordModal';
import { useStackedTables } from '@eskoolhub/ui/stacked-tables';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import {
  IconAudit,
  IconBilling,
  IconHealth,
  IconLogout,
  IconOverview,
  IconSchools,
  IconTickets,
  IconUsers,
} from './Icons';

interface NavEntry {
  to: string;
  label: string;
  icon: (props: { size?: number; fill?: string }) => ReactNode;
  badge?: number;
}

function NavRow({ entry }: { entry: NavEntry }) {
  const Icon = entry.icon;
  return (
    <NavLink to={entry.to} className={({ isActive }) => (isActive ? 'navitem navitem--active' : 'navitem')} end={entry.to === '/'}>
      {({ isActive }) => (
        <>
          <Icon size={17} fill={isActive ? 'var(--ink)' : 'rgba(255,255,255,.55)'} />
          <span>{entry.label}</span>
          {entry.badge ? <span className="navitem__count">{entry.badge}</span> : null}
        </>
      )}
    </NavLink>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const canSeeSchools = user?.permissions.schoolData ?? false;
  const canSeeTickets = user?.permissions.tickets ?? false;
  const canSeeBilling = user?.permissions.billing ?? false;
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  // Sidebar badges — cheap counts, refreshed on a slow interval. Only asks for
  // what this admin is allowed to see, so no request 403s in the background.
  const { data: counts } = useQuery({
    queryKey: ['sidebar-counts', canSeeSchools, canSeeTickets],
    queryFn: async () => {
      const [schools, tickets] = await Promise.all([
        canSeeSchools ? api.get<{ total: number }>('/api/schools?pageSize=1') : Promise.resolve(null),
        canSeeTickets ? api.get<{ total: number }>('/api/tickets') : Promise.resolve(null),
      ]);
      return { schools: schools?.total, tickets: tickets?.total };
    },
    refetchInterval: 60_000,
  });

  // Nav follows the account's permissions — a Support admin without billing
  // access never sees a Billing tab that would only 403.
  const platform: NavEntry[] = [
    ...(canSeeSchools
      ? [
          { to: '/', label: 'Overview', icon: IconOverview },
          { to: '/schools', label: 'Schools', icon: IconSchools, badge: counts?.schools },
        ]
      : []),
    ...(canSeeBilling ? [{ to: '/billing', label: 'Billing', icon: IconBilling }] : []),
    { to: '/system-health', label: 'System health', icon: IconHealth },
    ...(canSeeTickets
      ? [{ to: '/tickets', label: 'Support tickets', icon: IconTickets, badge: counts?.tickets }]
      : []),
  ];

  const access: NavEntry[] = isSuperAdmin
    ? [
        { to: '/admin-users', label: 'Admin users', icon: IconUsers },
        { to: '/audit', label: 'Audit log', icon: IconAudit },
      ]
    : [];

  // The sidebar is a drawer below 900px. Closing it on navigation matters: on a
  // phone it covers the page, so leaving it open after a tap would hide the
  // screen the person just asked for.
  // Cells learn their column name, so a phone can stack the rows.
  useStackedTables();

  const [navOpen, setNavOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const here = useLocation().pathname;
  useEffect(() => setNavOpen(false), [here]);

  return (
    <div className="shell" data-nav={navOpen ? 'open' : 'closed'}>
      <button
        type="button"
        className="shell__scrim"
        aria-label="Close the menu"
        onClick={() => setNavOpen(false)}
      />

      <aside className="sidebar">
        <div className="sidebar__brand">
          <img src="/brand/logo-wordmark.png" alt="ESkoolHub" />
          <span className="sidebar__badge">ADMIN</span>
        </div>

        <nav className="sidebar__nav">
          <div className="sidebar__group" style={{ paddingTop: 8 }}>
            Platform
          </div>
          {platform.map((entry) => (
            <NavRow key={entry.to} entry={entry} />
          ))}
          {access.length > 0 ? (
            <>
              <div className="sidebar__group">Access</div>
              {access.map((entry) => (
                <NavRow key={entry.to} entry={entry} />
              ))}
            </>
          ) : null}
        </nav>

        <div className="sidebar__user">
          <div className="avatar">{user?.initials ?? '··'}</div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: '#fff' }}>{user?.name}</div>
            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.55)' }}>
              {user?.role === 'SUPER_ADMIN'
                ? 'Super admin'
                : user?.permissions.billing
                  ? 'Support · billing'
                  : 'Support'}
            </div>
          </div>
          <button
            onClick={() => setChangingPassword(true)}
            aria-label="Change your password"
            title="Change your password"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 14a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
              <path d="M10.5 11.5 20 21m-3-3 2-2m-4-1 2-2" />
            </svg>
          </button>
          <button
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            aria-label="Sign out"
            title="Sign out"
          >
            <IconLogout />
          </button>
        </div>
      </aside>

      {changingPassword ? (
        <ChangePasswordModal onClose={() => setChangingPassword(false)} />
      ) : null}

      <main className="main">
        {/* Sits above the page, so it lands in every screen without each of
            them having to know the drawer exists. */}
        <button
          type="button"
          className="shell__menu"
          aria-label="Open the menu"
          aria-expanded={navOpen}
          onClick={() => setNavOpen(true)}
          style={{ position: 'fixed', top: 10, left: 12, zIndex: 20 }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
        {children}
      </main>
    </div>
  );
}

export function PageHeader({
  title,
  meta,
  actions,
}: {
  title: string;
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="topbar">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div className="topbar__title">{title}</div>
        {meta ? <div className="topbar__meta">{meta}</div> : null}
      </div>
      {actions ? <div className="topbar__actions">{actions}</div> : null}
    </header>
  );
}
